import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../utils/api';
import HelpTooltip, { HELP } from '../components/HelpTooltip';
import { X } from 'lucide-react';

const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);

// 商談結果は3つ（ADR 0020）。旧「後追い」は保留に統合したので新規では選べない。
const RESULTS = ['獲得', '失注', '保留'];
const RESULT_COLORS = { 獲得: 'var(--gain)', 失注: 'var(--loss)', 保留: 'var(--mark)', 後追い: 'var(--mark)' };
// 着座はしたがまだクローズされていない状態（ADR 0014）。キャッチャーはここで止めて保存できる。
const PENDING_RESULT = '未対応';

// 結果ごとに出す項目（ADR 0020）。common は3結果すべてで共通。
const ENUM_KEYS = [
  'power_company', 'household', 'meter', 'acquired_product', 'lost_reason',
  'solar_appt', 'plan_type', 'test_close', 'exclusion_reason', 'hold_reason',
  'contact_exchange', 'next_action',
];
// 失注理由がこれのときだけ「対象外理由」を出す
const EXCLUSION_TRIGGER = '対象外';

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(22,32,43,0.35)',
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};

// シートは「ヘッダー／スクロールする本文／固定フッター」の3段。
// 保存ボタンをフッターに置くことで、結果を選んで項目が増えても常に画面内に残る
// （以前は本文の一番下にあり、獲得を選ぶと約1,000px下に隠れて押せなかった）。
// 高さは .bottom-sheet（index.css）で 92dvh／92vh を出し分ける。
const sheet = {
  width: '100%', maxWidth: 540,
  background: 'var(--field)',
  borderRadius: '20px 20px 0 0',
  border: '1px solid var(--rule-strong)',
  boxShadow: 'none',
  display: 'flex', flexDirection: 'column', minHeight: 0,
};

// スクロールするのは本文だけ。overscrollBehavior: 'contain' で、
// 端まで来たときに背後の一覧が動く（＝フォームが動かない）のを防ぐ。
const sheetBody = {
  flex: 1, minHeight: 0, overflowY: 'auto',
  overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
  paddingBottom: 8,
};

const sheetFooter = {
  flexShrink: 0,
  padding: '12px 20px calc(12px + env(safe-area-inset-bottom, 0px))',
  borderTop: '1px solid var(--rule-strong)',
  background: 'var(--field)',
};

const label = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 6, display: 'block' };
const input = { width: '100%', padding: '10px 14px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, boxSizing: 'border-box' };
const section = { padding: '0 20px', marginBottom: 20 };

// 選択肢チップの共通部品。選択済みのものをもう一度押すと未選択に戻る。
// 現場はスマホなので、指で押せる高さ(44px以上)を確保する。
function ChipGroup({ title, hint, options, value, onChange, color = 'var(--mark)' }) {
  if (!options || options.length === 0) return null;
  return (
    <div style={section}>
      <label style={label}>
        {title}
        {hint && <span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>{hint}</span>}
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map(opt => {
          const active = value === opt;
          return (
            <button
              key={opt}
              onClick={() => onChange(active ? '' : opt)}
              style={{
                flex: '1 1 30%', minWidth: 96, minHeight: 46,
                padding: '10px 12px', borderRadius: 12, fontWeight: 700, fontSize: 'var(--t-note)',
                border: `1.5px solid ${active ? color : 'var(--ink-mute)'}`,
                background: active ? `color-mix(in srgb, ${color} 13%, transparent)` : 'transparent',
                color: active ? color : 'var(--ink-mute)',
                cursor: 'pointer', transition: 'all 120ms',
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function SeatMemoEditor({ memo, onClose, onSaved, user, defaultDate }) {
  const nowTime = new Date(Date.now() + 9 * 3600000).toISOString().slice(11, 16); // JST 'HH:MM'
  // 新規メモの日付。一覧が過去日を開いていればその日を引き継ぐ（ADR 0025）。
  const initialDate = memo?.memo_date || defaultDate || todayJst();
  // 時刻は「今」を既定にするが、過去日の追加では今の時刻に意味が無いので空にする。
  const initialTime = memo ? (memo.memo_time || '') : (initialDate === todayJst() ? nowTime : '');

  const [form, setForm] = useState({
    memo_date: initialDate,
    memo_time: initialTime,
    result: memo?.result === PENDING_RESULT ? '' : (memo?.result || ''),
    // 共通（3結果とも）
    solar_appt: memo?.solar_appt || '',
    plan_type: memo?.plan_type || '',
    current_power_company: memo?.current_power_company || '',
    household_type: memo?.household_type || '',
    customer_gender: memo?.customer_gender || '',
    meter_status: memo?.meter_status || '',
    note: memo?.note || '',
    // 獲得
    acquired_product: memo?.acquired_product || '',
    // 失注
    test_close: memo?.test_close || '',
    lost_reason: memo?.lost_reason || '',
    exclusion_reason: memo?.exclusion_reason || '',
    // 保留
    hold_reason: memo?.hold_reason || '',
    contact_exchanged: memo?.contact_exchanged || '',
    next_action: memo?.next_action || '',
    // 渡す相手（クローザー指名）。'' = 指名なし＝手が空いた人が拾う。ADR 0015
    assigned_closer_id: memo?.assigned_closer_id ? String(memo.assigned_closer_id) : '',
    // 有効商談かどうか。'' = 未選択 / '1' = 有効 / '0' = 有効ではない。ADR 0019
    valid_deal: memo?.valid_deal === 1 ? '1' : memo?.valid_deal === 0 ? '0' : '',
  });
  const [enums, setEnums] = useState({});
  const [closers, setClosers] = useState([]);
  const [saving, setSaving] = useState(false);
  const editedRef = useRef(false); // ユーザーが何か入力したか（未保存で閉じる誤操作を防ぐ）
  const resultRef = useRef(null);  // 商談結果の見出し（保存できない理由の場所へ連れて行く）
  const [hint, setHint] = useState(''); // 保存できなかった理由を画面内にも出す
  // 日付・時刻は自動で入るので既定は畳む。必須の「商談結果」より目立たせない。
  const [showDateTime, setShowDateTime] = useState(false);

  // 入力途中に閉じようとしたら確認する（背景タップ・×ボタン共通）。
  const attemptClose = () => {
    if (editedRef.current && !window.confirm('入力途中の内容が消えます。閉じてよろしいですか？')) return;
    onClose();
  };

  // 開いている間はボトムナビを消す（保存ボタンと同じ位置に来るため）。
  useEffect(() => {
    document.body.classList.add('sheet-open');
    return () => document.body.classList.remove('sheet-open');
  }, []);

  useEffect(() => {
    Promise.all(ENUM_KEYS.map(k => api.getEnum(k).then(v => [k, v]).catch(() => [k, []])))
      .then(results => {
        setEnums(Object.fromEntries(results.map(([k, v]) => [k, (v || []).map(x => x.value)])));
      });
  }, []);

  // 指名の選択肢。指名を決められるのは着座させた本人（新規は自分）と管理者だけ。
  const canAssign = !memo || memo.user_id === user?.id || user?.role === 'admin';
  useEffect(() => {
    if (!canAssign) return;
    // 取得に失敗しても入力は続けられるようにする（会場の電波が悪いことがある）。
    api.getClosers().then(list => setClosers(Array.isArray(list) ? list : [])).catch(() => {});
  }, [canAssign]);

  // 日付またぎ対策：シートを開いたまま日付が変わったら、新規メモで日付を今日から変えていない場合だけ
  // memo_date を新しい今日へ追従させる（過去日を選んでいる／編集中のメモはそのまま）。
  const todayRef = useRef(todayJst());
  useEffect(() => {
    const check = () => {
      const t = todayJst();
      if (t === todayRef.current) return;
      const prev = todayRef.current;
      todayRef.current = t;
      if (!memo) setForm((f) => (f.memo_date === prev ? { ...f, memo_date: t } : f));
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, [memo]);

  const set = (k, v) => { editedRef.current = true; setHint(''); setForm(f => ({ ...f, [k]: v })); };

  // 指名チップ（現場はスマホなので指で押せる高さを確保する）
  const closerChip = (active) => ({
    padding: '10px 14px', minHeight: 44, borderRadius: 10, fontWeight: 700, fontSize: 'var(--t-note)',
    border: `1.5px solid ${active ? 'var(--hold)' : 'var(--ink-mute)'}`,
    background: active ? 'var(--hold-bg)' : 'transparent',
    color: active ? 'var(--hold)' : 'var(--ink-mute)', cursor: 'pointer',
  });

  // キャッチャーは着座までが仕事なので、結果を入れずに保存できる（「対応待ち」になる）。
  // キャッチャーの入力は「渡す相手」だけ。結果もお客様の情報もクローザーが入れるので出さない。
  // すでにクローザーが結果を入れたメモは、キャッチャーには読み取り専用で見せる（触る用事がない）。
  const isCatcher = (user?.job_type || 'both') === 'catcher';
  const memoClosed = !!memo && !!memo.result && memo.result !== PENDING_RESULT;
  const catcherReadOnly = isCatcher && memoClosed;
  // 結果を選ぶまでは詳細項目を出さない（ADR 0020）。
  const r = form.result;

  const handleSave = async () => {
    // 結果なしで保存できるのは、キャッチャーか、渡す相手を指名したとき（ADR 0015）。
    // 弾くだけだと「なぜ保存できないのか」が分からないので、該当の欄まで戻してその場に出す。
    if (!form.result && !isCatcher && !form.assigned_closer_id) {
      setHint('商談結果を選ぶか、渡すクローザーを指名すると保存できます。');
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setHint('');
    setSaving(true);
    try {
      // ブースは「1日1ブース」。時間別入力で設定したその日のブースをサーバ側で自動継承する。
      const payload = {
        memo_date: form.memo_date,
        memo_time: form.memo_time || null,
        // 未選択のまま保存＝「対応待ち」。クローザーが後から結果を入れる。
        result: form.result || PENDING_RESULT,
        note: form.note || null,
        // 共通項目（結果を選んだときだけ入る）
        solar_appt: form.solar_appt || null,
        plan_type: form.plan_type || null,
        current_power_company: form.current_power_company || null,
        household_type: form.household_type || null,
        customer_gender: form.customer_gender || null,
        meter_status: form.meter_status || null,
        // 結果ごとの項目。別の結果に切り替えたときに前の入力が残らないよう、明示的に null を送る。
        acquired_product: r === '獲得' ? form.acquired_product || null : null,
        test_close:       r === '失注' ? form.test_close || null : null,
        lost_reason:      r === '失注' ? form.lost_reason || null : null,
        exclusion_reason: (r === '失注' && form.lost_reason === EXCLUSION_TRIGGER) ? form.exclusion_reason || null : null,
        hold_reason:       r === '保留' ? form.hold_reason || null : null,
        contact_exchanged: r === '保留' ? form.contact_exchanged || null : null,
        next_action:       r === '保留' ? form.next_action || null : null,
        // 有効商談は結果を入れる人（クローザー）が選ぶ。未選択は null のまま送る。
        valid_deal: form.valid_deal === '' ? null : Number(form.valid_deal),
      };
      // 指名は権限がある時だけ送る（クローザーが結果を入れる時に付け替わらないように）。
      if (canAssign) payload.assigned_closer_id = form.assigned_closer_id ? Number(form.assigned_closer_id) : null;
      if (memo) {
        await api.updateSeatMemo(memo.id, payload);
      } else {
        await api.createSeatMemo(payload);
      }
      onSaved();
    } catch (e) { setHint(e.message); }
    setSaving(false);
  };

  // 3つの結果で共通の項目（ADR 0020）。結果を選んだあとに出す。
  const commonFields = (
    <>
      <ChipGroup title="太陽光アポ" options={enums.solar_appt} value={form.solar_appt}
        onChange={v => set('solar_appt', v)} color="var(--hold)" />
      <ChipGroup title="種別" options={enums.plan_type} value={form.plan_type}
        onChange={v => set('plan_type', v)} color="var(--mark)" />
      <ChipGroup title="現在の電力会社" options={enums.power_company} value={form.current_power_company}
        onChange={v => set('current_power_company', v)} color="var(--mark)" />
      <ChipGroup title="世帯人数" options={enums.household} value={form.household_type}
        onChange={v => set('household_type', v)} color="var(--mark)" />

      {/* 商談相手は絵文字付きの固定3択 */}
      <div style={section}>
        <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          商談相手
          <HelpTooltip title={HELP.gender.title} body={HELP.gender.body} />
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { key: '男性',     color: 'var(--mark)' },
            { key: '女性',     color: 'var(--mark)' },
            { key: '男女両方', color: 'var(--mark)' },
          ].map(({ key, color }) => {
            const active = form.customer_gender === key;
            return (
              <button key={key} onClick={() => set('customer_gender', active ? '' : key)} style={{
                flex: 1, padding: '12px 4px', borderRadius: 12, fontWeight: 700, fontSize: 'var(--t-note)',
                border: `1.5px solid ${active ? color : 'var(--ink-mute)'}`,
                background: active ? `color-mix(in srgb, ${color} 13%, transparent)` : 'transparent',
                color: active ? color : 'var(--ink-mute)',
                cursor: 'pointer', transition: 'all 120ms',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                minHeight: 48,
              }}>
                {key}
              </button>
            );
          })}
        </div>
      </div>

      <ChipGroup title="マイページ・検針票確認" options={enums.meter} value={form.meter_status}
        onChange={v => set('meter_status', v)} color="var(--mark)" />

      <div style={section}>
        <label style={label}>メモ<span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>（任意）</span></label>
        <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={form.note}
          onChange={e => set('note', e.target.value)} placeholder="気づいたことを一言" />
      </div>
    </>
  );

  // document.body へ出す（ポータル）。
  // この画面は <main> → ページ切替アニメ用の <div> の中にある。アニメーションが当たった要素は
  // 「重なりの土台（stacking context）」を作るので、その中で z-index: 9000 と書いても、
  // 比べられるのは土台の中だけ。土台自体は z-index: auto なので、root にいるボトムナビ
  // （z-index: 800）のほうが上に描かれ、保存ボタンがナビの下に潜って押せなくなる。
  // （iOS Safari で発生。Chrome はアニメ終了後に土台を捨てるので気づけなかった）
  return createPortal(
    <div style={overlay} onClick={e => e.target === e.currentTarget && attemptClose()}>
      <div style={sheet} className="bottom-sheet">
        {/* ハンドル */}
        <div style={{ textAlign: 'center', paddingTop: 12, paddingBottom: 4 }}>
          <div style={{ width: 40, height: 4, background: 'var(--paper-sunk)', borderRadius: 99, display: 'inline-block' }} />
        </div>

        {/* タイトルバー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px 20px' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink)' }}>{memo ? '着座メモを編集' : '着座メモを追加'}</h2>
          <button onClick={attemptClose} style={{ background: 'var(--paper-sunk)', border: 'none', borderRadius: 10, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={18} color="var(--ink-soft)" />
          </button>
        </div>

        {/* ここから下だけがスクロールする。保存ボタンはフッターに固定。 */}
        <div style={sheetBody}>

        {/* クローザー対応済み（キャッチャーには読み取り専用） */}
        {catcherReadOnly && (
          <div style={section}>
            <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--gain-bg)', border: '1px solid #1E5540' }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--gain)', marginBottom: 6 }}>
                クローザーが対応済みです（{memo.result}{memo.acquired_product ? `・${memo.acquired_product}` : ''}）
              </div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
                この着座はあなたの実績として記録されています。内容を直したいときは管理者に連絡してください。
              </div>
            </div>
          </div>
        )}

        {/* 日付・時刻 */}
        {!catcherReadOnly && (
        <div style={section}>
          {showDateTime ? (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>日付</label>
                <input type="date" style={input} value={form.memo_date} onChange={e => set('memo_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>時刻 <span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>（時間別に自動反映）</span></label>
                <input type="time" style={input} value={form.memo_time} onChange={e => set('memo_time', e.target.value)} />
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowDateTime(true)} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 40,
              padding: '0 2px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 'var(--t-note)', color: 'var(--ink-soft)', textAlign: 'left',
            }}>
              <span className="num">{form.memo_date} {form.memo_time}</span>
              <span style={{ color: 'var(--mark)', fontWeight: 600 }}>日付・時刻を変更</span>
            </button>
          )}
        </div>
        )}

        {/* 商談結果（キャッチャーは結果を入れないので出さない） */}
        {!isCatcher && (
        <div style={section} ref={resultRef}>
          <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ whiteSpace: 'nowrap' }}>商談結果 <span style={{ color: 'var(--loss)' }}>*</span></span>
            <span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>（クローザーを指名すれば後回しでよい）</span>
            <HelpTooltip title={HELP.result.title} body={HELP.result.body} />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {RESULTS.map(v => {
              const active = form.result === v;
              const color = RESULT_COLORS[v];
              return (
                <button key={v} onClick={() => set('result', active ? '' : v)} style={{
                  flex: 1, padding: '12px 4px', borderRadius: 'var(--r-sm)', fontSize: 16, minHeight: 56,
                  fontWeight: active ? 700 : 600,
                  border: `${active ? 2 : 1}px solid ${active ? color : 'var(--rule-strong)'}`,
                  background: active ? `color-mix(in srgb, ${color} 14%, transparent)` : 'var(--field)',
                  color, cursor: 'pointer', transition: 'background-color 120ms, border-color 120ms',
                }}>
                  {v}
                </button>
              );
            })}
          </div>
          {/* 旧「後追い」のメモを開いたときだけ、今の値が分かるように出す */}
          {memo?.result === '後追い' && !form.result && (
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.6 }}>
              このメモは旧区分の<strong style={{ color: 'var(--mark)' }}>「後追い」</strong>です。保存すると選び直した結果に変わります。
            </div>
          )}
        </div>
        )}

        {/* 渡す相手（クローザー指名）。結果が未選択＝この着座を人に渡すときだけ出す。ADR 0015 */}
        {canAssign && !form.result && (
          <div style={section}>
            <label style={label}>渡す相手（クローザー）</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button onClick={() => set('assigned_closer_id', '')} style={closerChip(!form.assigned_closer_id)}>
                指名なし（全員へ）
              </button>
              {closers.map(c => {
                const active = form.assigned_closer_id === String(c.id);
                return (
                  <button key={c.id} onClick={() => set('assigned_closer_id', active ? '' : String(c.id))} style={closerChip(active)}>
                    {c.name}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.6 }}>
              {form.assigned_closer_id
                ? <>この着座は<strong style={{ color: 'var(--hold)' }}>指名した人の「対応待ち」にだけ</strong>表示されます。</>
                : <>指名なしのままだと、手が空いたクローザーが誰でも拾えます。</>}
              {isCatcher && <><br />結果・お客様の情報はクローザーが入力します。</>}
            </div>
            {closers.length === 0 && (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 6, lineHeight: 1.6 }}>
                指名できる相手がいません。管理者が「ユーザー管理」で役割を<strong>クローザー</strong>または
                <strong>キャッチャー&amp;クローザー</strong>に設定してください。
              </div>
            )}
          </div>
        )}

        {/* 有効商談かどうか（結果を入れる人が選ぶ。キャッチャーには出さない）。ADR 0019 */}
        {!isCatcher && !catcherReadOnly && r && (
          <div style={section}>
            <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              商談の質 <span style={{ color: 'var(--ink-mute)', fontWeight: 400 }}>（任意）</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { key: '1', text: '有効商談',     color: 'var(--gain)' },
                { key: '0', text: '有効じゃない', color: 'var(--hold)' },
              ].map(({ key, text, color }) => {
                const active = form.valid_deal === key;
                return (
                  <button key={key} onClick={() => set('valid_deal', active ? '' : key)} style={{
                    flex: 1, padding: '12px 4px', borderRadius: 12, fontWeight: 700, fontSize: 14, minHeight: 48,
                    border: `1.5px solid ${active ? color : 'var(--ink-mute)'}`,
                    background: active ? `color-mix(in srgb, ${color} 13%, transparent)` : 'transparent',
                    color: active ? color : 'var(--ink-mute)', cursor: 'pointer', transition: 'all 120ms',
                  }}>
                    {text}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== 獲得パターン ===== */}
        {!catcherReadOnly && r === '獲得' && (
          <>
            <ChipGroup title="獲得商材" options={enums.acquired_product} value={form.acquired_product}
              onChange={v => set('acquired_product', v)} color="var(--gain)" />
            {commonFields}
          </>
        )}

        {/* ===== 失注パターン ===== */}
        {!catcherReadOnly && r === '失注' && (
          <>
            <ChipGroup title="テスクロ" options={enums.test_close} value={form.test_close}
              onChange={v => set('test_close', v)} color="var(--loss)" />
            <ChipGroup title="失注理由" options={enums.lost_reason} value={form.lost_reason}
              onChange={v => set('lost_reason', v)} color="var(--loss)" />
            {/* 「対象外」を選んだときだけ深掘りする */}
            {form.lost_reason === EXCLUSION_TRIGGER && (
              <ChipGroup title="対象外理由" options={enums.exclusion_reason} value={form.exclusion_reason}
                onChange={v => set('exclusion_reason', v)} color="var(--hold)" />
            )}
            {commonFields}
          </>
        )}

        {/* ===== 保留パターン ===== */}
        {!catcherReadOnly && r === '保留' && (
          <>
            <ChipGroup title="保留理由" options={enums.hold_reason} value={form.hold_reason}
              onChange={v => set('hold_reason', v)} color="var(--mark)" />
            <ChipGroup title="連絡交換" options={enums.contact_exchange} value={form.contact_exchanged}
              onChange={v => set('contact_exchanged', v)} color="var(--mark)" />
            <ChipGroup title="次回対応" options={enums.next_action} value={form.next_action}
              onChange={v => set('next_action', v)} color="var(--mark)" />
            {commonFields}
          </>
        )}

        </div>{/* /スクロールする本文 */}

        {/* 保存ボタン（フッター固定）。項目が増えても押せる位置から動かない。 */}
        <div style={sheetFooter}>
          {hint && (
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--loss)', fontWeight: 700, lineHeight: 1.5, marginBottom: 10 }}>
              {hint}
            </div>
          )}
          {/* 下まで来ると上の選択が見えないので、何として保存されるかをボタンの上に出す。 */}
          {!catcherReadOnly && !hint && (
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', fontWeight: 600, lineHeight: 1.5, marginBottom: 8 }}>
              {form.result
                ? <>結果：<strong style={{ color: RESULT_COLORS[form.result] }}>{form.result}</strong> として記録します</>
                : <>結果は未選択。<strong style={{ color: 'var(--hold)' }}>対応待ち</strong>として保存され、クローザーが結果を入れます</>}
            </div>
          )}
          {catcherReadOnly ? (
            <button onClick={onClose} style={{
              width: '100%', padding: '14px', borderRadius: 14, minHeight: 52,
              background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)',
              color: 'var(--ink)', fontWeight: 700, fontSize: 16, cursor: 'pointer',
            }}>
              閉じる
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} style={{
              width: '100%', padding: '14px', borderRadius: 14, minHeight: 52,
              background: 'var(--grad-primary)', border: 'none',
              color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer',
              opacity: saving ? 0.6 : 1, boxShadow: '0 4px 20px var(--mark)',
            }}>
              {saving ? '保存中...' : (memo ? '更新する' : '保存する')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
